import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Gift, Copy, Coins, Users, Trophy, Crown, Check, Share2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

export default function Referrals() {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    api.get("/referrals/me").then((r) => setData(r.data)).catch(() => {});
    api.get("/referrals/leaderboard").then((r) => setBoard(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const link = data ? `${window.location.origin}/auth?ref=${data.code}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true); toast.success("Invite link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { toast.error("Could not copy — long-press to copy manually"); }
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Join me on Pleasure Empire", text: "Come play in the Empire 🔥", url: link }); }
      catch (e) {}
    } else { copy(); }
  };

  const shareText = "Come play in the Empire 🔥 Join me on Pleasure Empire";
  const socials = [
    { id: "whatsapp", label: "WhatsApp", color: "#25D366",
      href: () => `https://wa.me/?text=${encodeURIComponent(`${shareText} ${link}`)}` },
    { id: "x", label: "X", color: "#e7e7e7",
      href: () => `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(link)}` },
    { id: "facebook", label: "Facebook", color: "#1877F2",
      href: () => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}` },
    { id: "telegram", label: "Telegram", color: "#229ED9",
      href: () => `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(shareText)}` },
  ];
  const shareVia = (s) => window.open(s.href(), "_blank", "noopener,noreferrer");

  if (!data) return <div className="p-12 text-[#A0A0A0]">Loading…</div>;

  return (
    <div className="p-6 md:p-12 max-w-3xl mx-auto">
      <div className="overline mb-1">Grow the Empire</div>
      <h2 className="font-heading text-3xl md:text-4xl mb-2 flex items-center gap-3"><Gift className="text-[#D4AF37]" /> Invite Friends, Earn Rewards</h2>
      <p className="text-[#A0A0A0] text-sm mb-8 max-w-xl">
        Share your link. Earn <span className="text-[#D4AF37]">{data.coins_per_referral} Empire Coins</span> for every friend who joins —
        and every <span className="text-[#D4AF37]">{data.referrals_per_free_month}</span> invites unlocks a <span className="text-[#D4AF37]">free month of VIP Creator membership</span>.
      </p>

      {/* Stats dashboard */}
      <div className="grid grid-cols-3 gap-3 mb-8" data-testid="referral-stats">
        {[
          { label: "Friends invited", value: data.count || 0, testid: "stat-invited" },
          { label: "Made a purchase", value: data.converted || 0, testid: "stat-converted" },
          { label: "Coins earned", value: (data.coins_earned || 0).toLocaleString(), testid: "stat-coins-earned" },
        ].map((s) => (
          <div key={s.label} data-testid={s.testid} className="pe-card rounded-2xl p-4 text-center">
            <div className="text-2xl md:text-3xl font-heading gold-text">{s.value}</div>
            <div className="text-[11px] text-[#A0A0A0] mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Share card */}
      <motion.div
        data-testid="referral-share-card"
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl p-7 md:p-9 mb-8 border border-[#D4AF37]/40"
        style={{ background: "radial-gradient(120% 120% at 0% 0%, #1c1608 0%, #0A0A0A 55%)" }}>
        <div className="orb" style={{ width: 220, height: 220, top: -60, right: -40, background: "rgba(212,175,55,0.28)" }} />
        <div className="relative">
          <Sparkles className="text-[#D4AF37] mb-3" size={26} />
          <h3 className="font-heading text-3xl md:text-4xl leading-tight mb-2">
            Invite <span className="gold-text">{data.referrals_per_free_month} friends</span>,<br />unlock a <span className="gold-text">free month of VIP</span>
          </h3>
          <p className="text-sm text-[#C7C7C7] max-w-md mb-6">
            Every friend who joins earns you {data.coins_per_referral} Empire Coins — and they get a bonus too. Spread the desire.
          </p>

          {/* progress to next reward */}
          <div className="mb-6 max-w-md">
            <div className="flex justify-between text-[11px] text-[#A0A0A0] mb-1">
              <span>Progress to next free VIP month</span>
              <span>{data.referrals_per_free_month - data.referrals_to_next_reward}/{data.referrals_per_free_month}</span>
            </div>
            <div className="h-2 rounded-full bg-[#2A2A2A] overflow-hidden">
              <motion.div className="h-full rounded-full" style={{ background: "linear-gradient(90deg,#B8860B,#D4AF37)" }}
                initial={{ width: 0 }}
                animate={{ width: `${((data.referrals_per_free_month - data.referrals_to_next_reward) / data.referrals_per_free_month) * 100}%` }}
                transition={{ duration: 0.8 }} />
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button data-testid="share-card-share" onClick={share} className="btn-gold px-6 py-3 rounded-full text-sm flex items-center gap-2">
              <Share2 size={16} /> Share my invite
            </button>
            <button data-testid="share-card-copy" onClick={copy} className="btn-outline-gold px-6 py-3 rounded-full text-sm flex items-center gap-2">
              {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copied!" : "Copy link"}
            </button>
          </div>

          {/* One-tap social share */}
          <div className="mt-5">
            <div className="text-[11px] text-[#A0A0A0] mb-2">Or share instantly to</div>
            <div className="flex flex-wrap gap-2" data-testid="social-share-row">
              {socials.map((s) => (
                <button key={s.id} data-testid={`share-${s.id}`} onClick={() => shareVia(s)}
                  className="px-4 py-2 rounded-full text-xs font-medium flex items-center gap-2 border border-[#2A2A2A] hover:border-[#D4AF37]/60 transition-colors"
                  style={{ color: s.color }}>
                  <span className="w-2 h-2 rounded-full" style={{ background: s.color }} /> {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div data-testid="ref-stat-invites" className="pe-card rounded-2xl p-5">
          <Users className="text-[#D4AF37] mb-2" size={22} />
          <div className="font-heading text-3xl gold-text">{data.count}</div>
          <div className="text-xs text-[#A0A0A0]">Friends invited</div>
        </div>
        <div data-testid="ref-stat-coins" className="pe-card rounded-2xl p-5">
          <Coins className="text-[#D4AF37] mb-2" size={22} />
          <div className="font-heading text-3xl gold-text">{data.coins}</div>
          <div className="text-xs text-[#A0A0A0]">Empire Coins</div>
        </div>
        <div data-testid="ref-stat-next" className="pe-card rounded-2xl p-5">
          <Crown className="text-[#D4AF37] mb-2" size={22} />
          <div className="font-heading text-3xl gold-text">{data.referrals_to_next_reward}</div>
          <div className="text-xs text-[#A0A0A0]">Invites to free VIP month</div>
        </div>
      </div>

      {/* Invite link */}
      <div className="pe-card rounded-2xl p-5 mb-8">
        <div className="text-sm text-white font-medium mb-2">Your invite link</div>
        <div className="flex gap-2">
          <input data-testid="ref-link" readOnly value={link}
            className="pe-input flex-1 rounded-lg px-4 py-3 text-sm text-[#D4AF37]" />
          <button data-testid="ref-copy" onClick={copy} className="btn-outline-gold px-4 rounded-lg flex items-center gap-2">
            {copied ? <Check size={18} /> : <Copy size={18} />}
          </button>
          <button data-testid="ref-share" onClick={share} className="btn-gold px-5 rounded-lg text-sm">Share</button>
        </div>
        <div className="text-xs text-[#A0A0A0] mt-2">New members also get {50} bonus coins when they use your link.</div>
      </div>

      {/* Leaderboard */}
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="text-[#D4AF37]" size={20} />
        <h3 className="font-heading text-2xl">Top Inviters</h3>
      </div>
      {board.length === 0 ? (
        <div data-testid="ref-board-empty" className="pe-card rounded-2xl p-8 text-center text-[#A0A0A0] text-sm">
          No inviters yet — be the first to top the leaderboard!
        </div>
      ) : (
        <div className="space-y-2">
          {board.map((b) => (
            <motion.div key={b.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
              data-testid={`ref-board-${b.rank}`}
              className={`pe-card rounded-2xl p-4 flex items-center gap-4 ${b.is_me ? "border border-[#D4AF37]/60" : ""}`}>
              <div className={`font-heading text-2xl w-8 text-center ${b.rank <= 3 ? "gold-text" : "text-[#A0A0A0]"}`}>{b.rank}</div>
              <div className="w-11 h-11 rounded-full overflow-hidden bg-[#1A1A1A] border border-[#2A2A2A] flex-shrink-0">
                {b.photo && <img src={b.photo} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium truncate flex items-center gap-2">
                  {b.name} {b.is_me && <span className="text-[10px] text-[#D4AF37]">(you)</span>}
                </div>
                {b.top_inviter && <div className="text-[11px] text-[#D4AF37] flex items-center gap-1"><Trophy size={11} /> Top Inviter</div>}
              </div>
              <div className="text-right">
                <div className="font-heading text-xl gold-text">{b.referral_count}</div>
                <div className="text-[10px] text-[#A0A0A0]">invites</div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
